import {
  createUserService,
  deleteUserService,
  getUserByIdService,
  getUsersService,
  getUsersStatsService,
  resetUserPasswordService,
  toggleUserStatusService,
  updateUserService,
} from "@/services/users.service";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";

const USERS_KEY = ["users"] as const;
const USERS_STATS_KEY = ["users", "stats"] as const;

interface UsersFilters {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  kycStatus?: string;
  country?: string;
}

export const useUsers = (filters: UsersFilters = {}) =>
  useQuery({
    queryKey: [...USERS_KEY, filters],
    queryFn: async () => {
      const {data} = await getUsersService(filters);
      if (data === undefined) throw new Error("No data");
      return data;
    },
  });

export const useUser = (userId: string) =>
  useQuery({
    queryKey: [...USERS_KEY, userId],
    queryFn: async () => {
      const {data} = await getUserByIdService(userId);
      if (data === undefined) throw new Error("No data");
      return data;
    },
    enabled: !!userId,
  });

export const useUsersStats = () =>
  useQuery({
    queryKey: USERS_STATS_KEY,
    queryFn: async () => {
      const {data} = await getUsersStatsService();
      if (data === undefined) throw new Error("No data");
      return data;
    },
  });

export const useCreateUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {email: string; firstName: string; lastName: string; password: string}) =>
      createUserService(body),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: USERS_KEY});
    },
  });
};

export const useUpdateUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({userId, body}: {userId: string; body: Record<string, unknown>}) => updateUserService(userId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: USERS_KEY});
    },
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => deleteUserService(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: USERS_KEY});
    },
  });
};

export const useToggleUserStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({userId, enabled}: {userId: string; enabled: boolean}) => toggleUserStatusService(userId, enabled),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: USERS_KEY});
    },
  });
};

export const useResetUserPassword = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => resetUserPasswordService(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: USERS_KEY});
    },
  });
};
